import { useEffect, useRef, useState } from "react";
import { Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const STYLES = ["moderno e colorato", "elegante e minimale", "festoso con coriandoli", "natalizio", "estivo e luminoso", "retrò anni '80", "fotografico realistico"];
const JOB_KEY = (id) => `vw_screens_job_${id}`;

export function ScreensAllAiDialog({ contestId, customFormats, onApply, prominent = false }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState(STYLES[0]);
  const [prompt, setPrompt] = useState("");
  const [animateIdle, setAnimateIdle] = useState(true);
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  const poll = (jobId) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const j = await api.aiScreensAllStatus(contestId, jobId);
        setJob(j);
        if (j.status !== "running") {
          clearInterval(timer.current);
          localStorage.removeItem(JOB_KEY(contestId));
          if (j.status === "done") {
            onApply(j.screens);
            toast.success("Videate generate e inserite: ricordati di salvare la grafica");
            setOpen(false);
          } else toast.error(j.error || "Generazione non riuscita");
        }
      } catch (e) { console.debug("ScreensAllAi: polling fallito, riprovo", e); }
    }, 3000);
  };

  useEffect(() => {
    const saved = localStorage.getItem(JOB_KEY(contestId));
    if (saved) { setOpen(true); poll(saved); }
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId]);

  const run = async () => {
    try {
      const r = await api.aiScreensAll(contestId, { style, prompt, animate_idle: animateIdle, custom_formats: customFormats || [] });
      localStorage.setItem(JOB_KEY(contestId), r.job_id);
      setJob({ status: "running", done: 0, total: r.total || 12 });
      poll(r.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Avvio non riuscito");
    }
  };

  const total = 4 * (3 + (customFormats || []).length);
  const running = job?.status === "running";
  const pct = job ? Math.round(((job.done || 0) / (job.total || total)) * 100) : 0;
  return (
    <>
      <Button size="sm" variant={prominent ? "default" : "outline"} onClick={() => setOpen(true)} className={prominent ? "gap-1.5 bg-violet-600 hover:bg-violet-700 text-white" : "gap-1.5 border-violet-500/40 text-violet-300 hover:bg-violet-500/10"} data-testid={prominent ? "media-ai-backgrounds-open" : "screens-all-ai-open"}>
        <Wand2 className="h-3.5 w-3.5" /> {prominent ? "Genera sfondi con l'IA" : "Genera tutte con AI"}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!running) setOpen(v); }}>
        <DialogContent data-testid="screens-all-ai-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-400" /> Genera tutte le {total} videate</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Una sola serie coerente: 4 illustrazioni (attesa, gioco, vincita, perdita) nello stesso stile, ritagliate nei 3 formati. Le celle attuali verranno sostituite; poi salva la grafica.</p>
          {running ? (
            <div className="space-y-2" data-testid="screens-all-ai-progress">
              <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} /></div>
              <p className="text-xs text-muted-foreground text-center">{job.done || 0} / {job.total || total} videate · circa {Math.max(1, Math.round(((job.total || total) - (job.done || 0)) / 4)).toFixed(0)} min rimanenti. Puoi chiudere la pagina: al ritorno il lavoro riprende.</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Stile</Label>
                <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="screens-all-ai-style">
                  {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Indicazioni aggiuntive (facoltative)</Label>
                <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Es. tema supermercato, colori rosso e bianco…" data-testid="screens-all-ai-prompt" />
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer rounded-lg bg-secondary/40 p-3">
                <input type="checkbox" checked={animateIdle} onChange={(e) => setAnimateIdle(e.target.checked)} className="h-4 w-4 accent-violet-500 mt-0.5" data-testid="screens-all-ai-animate" />
                <span><b>Videate di attesa come GIF animate</b> <span className="block text-muted-foreground">Le altre fasi restano immagini statiche (più leggere e nitide).</span></span>
              </label>
              <Button onClick={run} className="w-full gap-2 bg-violet-600 hover:bg-violet-500" data-testid="screens-all-ai-run">
                <Wand2 className="h-4 w-4" /> Genera le {total} videate (2-5 min)
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
